import { useEffect, useRef } from 'react';
import useSyncOfflineData from 'src/utils/connectivity/useSyncOfflineData';
import { useToast } from 'src/app/useToast';
import { useSWService } from 'src/api/sw/useSWService';
import { useBunkerContext } from 'src/context/BunkerContext';

const useBackgroundSyncRecovery = () => {
    const { syncOfflineData } = useSyncOfflineData();
    const { toastError } = useToast();
    const { refreshRequestsContaining } = useSWService();
    const { setIsSaving } = useBunkerContext();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handledDeletedGuidsRef = useRef<Set<string>>(new Set());
    const refreshRequestsContainingRef = useRef(refreshRequestsContaining);

    useEffect(() => {
        refreshRequestsContainingRef.current = refreshRequestsContaining;
    });

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'BACKGROUND_SAVE_COMPLETED') {
                const endpoint: string = event.data.endpoint ?? '';
                if (endpoint === 'UpsertBunkering') {
                    void refreshRequestsContainingRef.current(['getBunkerings']);
                    // Delay clearing isSaving so the merge guard in BunkerRegistrationContainer
                    // remains active while React processes the incoming refresh data.
                    setTimeout(() => setIsSaving(false), 500);
                }
                return;
            }

            if (event.data?.type !== 'BACKGROUND_SAVE_FAILED') return;

            const errorType = event.data.errorType;
            const url: string = event.data.url ?? '';
            console.warn('Background save failed:', event.data);

            // Clear bunkering save state immediately on any failure for UpsertBunkering
            if (url.includes('UpsertBunkering')) {
                setIsSaving(false);
            }

            if (errorType === 'http') {
                const status = event.data.status;

                // 404 on save endpoints — report could not be found on the server.
                // Do NOT delete from local state; actual deletions are handled via SignalR.
                if (status === 404 && (url.includes('SaveNoonReportSection') || url.includes('UpdateSectionStatus'))) {
                    try {
                        const noonReportGuid = new URL(url).searchParams.get('noonReportGuid');
                        if (noonReportGuid && !handledDeletedGuidsRef.current.has(noonReportGuid)) {
                            handledDeletedGuidsRef.current.add(noonReportGuid);
                            setTimeout(() => handledDeletedGuidsRef.current.delete(noonReportGuid), 10000);
                            void refreshRequestsContainingRef.current(['getNoonReportsForShip']);
                            toastError('Save failed — the report could not be found on the server. Please refresh and try again.');
                        }
                    } catch (_) {
                        // URL parsing failed — fall through to generic error
                        toastError('Save failed — server returned an error. Please try again.');
                    }
                    return;
                }

                // Server rejected the request — notify user, don't retry
                toastError('Save failed — server returned an error. Please try again.');
                return;
            }

            // Network error (or legacy message without errorType) — trigger sync
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                syncOfflineData();
                debounceTimerRef.current = null;
            }, 2000);
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [syncOfflineData, toastError, setIsSaving]);
};

export default useBackgroundSyncRecovery;
